// New-user onboarding tour — "Sayer-led, interaction-gated tour".
//
// The tour creates a real (fixture-backed) forecast, then walks the user through
// it ONE STEP AT A TIME. Each step explains something and waits for the user to
// actually do it (click the probability, hover a trigger, branch, open a panel)
// before advancing — Sayer narrates, the user drives. There is no overlay or
// shadow demo mode, so it can never diverge from real behavior.
// See docs/onboarding-tour-plan.md.

// The canned question. Chosen to stay valid ≥9 months (resolution 2027-06-09)
// so the baked fixture doesn't go stale and trip submit()'s past-resolution
// guard. MUST match tour-fixture.json's `question`.
export const TOUR_QUESTION = "Will the Fed cut rates at the June 2027 FOMC meeting?";

// Substring of a real fixture event title — the soft-inflation trigger the tour
// suggests branching on. dispatchIntent matches titles by substring.
export const TOUR_BRANCH_EVENT = "Core PCE inflation drops";

// Sayer's intro line, shown as the offer (with Continue / Skip tour buttons).
export const TOUR_INTRO =
  "Welcome to Sayer. Click Continue for a quick tour or Skip to start forecasting.";

// Shown right after the user opts in, just before the example question is typed
// into the chat box and submitted for them.
export const TOUR_QUESTION_FORMAT = "We'll create an example forecast for you.";

// localStorage key tracking whether the user has seen the tour. Values:
// "completed" | "skipped" | "interrupted" | "tips" (introduced by first-contact tips
// instead, onboarding/tips.js). Absent ⇒ first visit.
export const ONBOARDING_KEY = "sooth-onboarding-state";

export function getOnboardingState() {
  try {
    return localStorage.getItem(ONBOARDING_KEY);
  } catch {
    return null;
  }
}
export function setOnboardingState(state) {
  try {
    localStorage.setItem(ONBOARDING_KEY, state);
  } catch {
    /* private mode / quota */
  }
}
export function hasSeenOnboarding() {
  return getOnboardingState() != null;
}

// The steps. Each step:
//   say        — narration string, or fn(ctx) → string (ctx carries live values
//                like the forecast probability). Omit for a silent gate.
//   legend     — legend-only step: render the Slice / Size trigger key as the
//                message instead of narration.
//   await      — the user action that advances the step (a key the host fires
//                via signalTourAction):
//                  "next"/"continue"  informational; advance on the Next button
//                  "report"           the report modal opens (user clicked the %)
//                  "report_closed"    the report modal closes again
//                  "hover"            the user hovers a trigger
//                  "branch"           the user creates a branch
//                  "panel_opened"     the user opens an edge drawer (log /
//                                     contract / time series)
//                  "panels_settled"   all drawers closed and quiet for ~2s
//                                     (silent gate before the wrap-up line)
//                Omitted + no hold ⇒ terminal step (no wait; tour ends after it).
//   hold       — auto-advance after N ms instead of awaiting an action.
//   spotlight  — element(s) to highlight with the blinking green lightbulb
//                ("resolution" | "trigger" | "tab").
//   examples   — reveal the example questions (terminal).
//   noCount    — no step number (e.g. the report-summary aside).
//   pauseAfter — ms delay before the NEXT step after this one's action resolves.
export const SAYER_STEPS = [
  {
    // Plain string, not fn(ctx): the line no longer interpolates the fixture
    // probability. `ctx.pct` is still supplied by the host for any future fn-style
    // step — see runSayerTour's typeof check, which accepts either shape.
    say: "Your forecast is ready. **Click the forecast on the right of the timeline** to open the full report.",
    await: "report",
    spotlight: "resolution",
    pauseAfter: 2500, // wait ~2.5s after the report opens before the summary line
  },
  {
    // Shown while the report is open (the chat is raised above it during the
    // tour so this stays visible). Unnumbered — it's a contextual aside.
    say: "The report explains the forecast and the reasoning behind it. **Click outside it** to close it.",
    await: "report_closed",
    noCount: true,
    pauseAfter: 1000, // wait 1s after the report closes before the triggers line
    onResume: (ctx) => ctx.openReport?.(), // reload landed here → re-open the report
  },
  {
    say: "Each circle on the timeline is a trigger: something that could happen and move the forecast.",
    hold: 5000, // wait 5s before the "hover over a trigger" line
    spotlight: "trigger",
  },
  {
    say: "**Hover over a trigger** to see its details.",
    await: "hover",
    spotlight: "trigger",
    pauseAfter: 1000, // wait 1s after the hover before the next line
  },
  {
    // The intro line AND the Slice / Size key are one message. Waits for
    // the user to click Continue before the "click a trigger" step.
    say: "A trigger's size and slice tell you two things:",
    legend: true,
    await: "continue",
    spotlight: "trigger",
  },
  {
    say: "**Click a trigger**, then choose **If this happens** to see how it would change the forecast.",
    await: "branch",
    spotlight: "trigger",
    pauseAfter: 1000, // wait 1s after the branch is made before the next line
  },
  {
    say: "That scenario shows the new chance if the trigger happens. You can also **double-click anywhere on the timeline** to add your own trigger. Where you click sets its date.",
    // Paced for two sentences, the second of which carries the double-click
    // affordance — the one thing here a user will not discover on their own.
    hold: 8500,
  },
  {
    say: "There's more in the icons on the left. **Open your forecasts** to explore.",
    await: "panel_opened",
    spotlight: "tab",
  },
  // Silent gate: wait until the user has closed the drawer and not reopened
  // another for 2 seconds, so "Those are the basics" doesn't interrupt browsing.
  { await: "panels_settled" },
  {
    say: "Those are the basics. Now ask your own question below.",
    examples: true,
    noCount: true,
  },
];

// Message appended when the user skips the tour (at the offer or mid-tour).
export const TOUR_SKIP_MSG = "You're all set. You can replay the tour anytime from Settings.";

// Appended if the user bails out mid-tour (asks their own question). Same copy
// as the skip message today — aliased so the two can't silently diverge.
export const TOUR_INTERRUPT_MSG = TOUR_SKIP_MSG;

// Post-tour sequence around the user's first own forecast.
// 1. On submit: a note that a contract will be created to review.
export const POST_TOUR_CONTRACT_NOTE =
  "Next, Sayer will check your question and show it to you to review.";
// 2. ~2s after the preprocess/contract panel is dismissed (continue or timeout).
export const POST_TOUR_GENERATING_NOTE = "This usually takes a minute or two.";
// 3. When the forecast lands.
export const POST_TOUR_TIP = "Your forecast is ready.";
// 4. 3s after the forecast lands — the bug/feature note (flashes the bug icon).
export const POST_TOUR_TIP_BUG =
  "One last thing: to report a problem or suggest an idea, use **Send feedback** to reach the team.";
// 5. After the user clicks the "End Tour" button on the bug note.
export const POST_TOUR_REPLAY = "You can replay this tour anytime from Settings.";
// 6. 45s after the replay tip IF the user hasn't submitted anything — a gentle
//    nudge to try their own forecast (cancelled if they submit one first).
export const POST_TOUR_NUDGE =
  "Ready when you are. Ask a yes-or-no question with a clear end date.";

// Deferred one-shot reminder of the add-your-own-trigger gesture, posted from a LATER
// forecast rather than during the tour. Eligibility (tour completers, >=2 own forecasts,
// >=1 day elapsed, never used the gesture) lives in lib/forecast/customTriggerNudge.js.
//
// Phrased as an offer, not an observation. "We noticed you have not tried..." is the
// version that reads as surveillance for a tip this small; naming the gesture and what
// it does is the version that reads as help.
export const CUSTOM_TRIGGER_REMINDER =
  "One thing worth trying: **double-click anywhere on the timeline** to add your own trigger. Where you click sets its date, and Sayer explores the scenario from there.";

export function makeTourToken() {
  return { cancelled: false };
}

// Walk the steps. `say(text)` appends a tour-narration message; `waitFor(key,
// token)` returns a Promise the host resolves when the user performs the action;
// `ctx` carries live values for fn-style `say`; `onStepChange(step)` lets the
// host reflect what it's waiting for (Next button, hints); `onDone()` fires only
// when the tour runs to the end (the host handles skip/interrupt states).
// Cancellable sleep — checks the token every 100ms so a skip mid-hold stops
// the tour promptly instead of waiting out the full duration.
async function holdFor(ms, token) {
  for (let t = 0; t < ms && !token.cancelled; t += 100) {
    await new Promise((r) => setTimeout(r, Math.min(100, ms - t)));
  }
}

export async function runSayerTour({
  say,
  waitFor,
  ctx,
  token,
  onStepChange,
  onDone,
  startIndex = 0,
  resuming = false,
}) {
  for (let i = startIndex; i < SAYER_STEPS.length; i++) {
    const step = SAYER_STEPS[i];
    if (token.cancelled) return;
    // On a reload-resume the step's narration is already in the restored chat, so
    // we must NOT re-emit it (that would duplicate the message and "change" the
    // chat). Re-arm the step's interactive state only. `resuming` is passed by the
    // host (true for a reload into a shown step, at ANY index including 0 — the old
    // `startIndex > 0` test wrongly re-emitted step 0); a fresh run passes false.
    const resumingThisStep = i === startIndex && resuming;
    if (resumingThisStep) {
      if (step.onResume) {
        try {
          await step.onResume(ctx);
        } catch {
          continue; // skip-forward: onResume couldn't rebuild this step's precondition (e.g. re-open the report),
          // so advance to the next step instead of awaiting an action that can never fire
        }
      }
    } else {
      const text = typeof step.say === "function" ? step.say(ctx) : step.say;
      // Push the message when there's narration OR a legend to render (the triggers
      // step is legend-only — the "How to read the triggers" card IS the message).
      if (text || step.legend) say(text || "", { legend: !!step.legend });
    }
    onStepChange?.(step);
    if (step.await) {
      await waitFor(step.await, token);
      if (token.cancelled) return;
    } else if (step.hold) {
      await holdFor(step.hold, token);
      if (token.cancelled) return;
    }
    if (step.pauseAfter) {
      await holdFor(step.pauseAfter, token);
      if (token.cancelled) return;
    }
  }
  onStepChange?.(null);
  if (!token.cancelled) onDone?.();
}
